import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import Typography from 'app/ui/components/atoms/typography';
import { YellowDot } from 'app/ui/components/atoms/yellow-dot';

export type NavigationCardProps = {
  url?: string;
  title?: string;
  content?: string;
  image?: string;
  isActive?: boolean;
  isNew?: boolean;
};

const CardLink = styled(Link)`
  position: relative;
  text-decoration: none;
  display: block;
  width: 100%;
  height: 100%;
`;

const Container = styled.div<{ $backgroundImage?: string; $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  min-height: 4.5rem;
  background-color: ${({ theme }) => theme.colors.black};
  ${({ $backgroundImage, theme, $isActive = true }) =>
    $backgroundImage &&
    `
    background-image: url(${$backgroundImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), transparent;
      border-radius: 0.5rem;
      z-index: 1;
      transition: 0.15s ease-in-out;
    }
    
    &:hover::before {
      background: ${
        $isActive
          ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), ${theme.colors.focoltoneWithTransparencyHover}`
          : 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), transparent'
      };
    }
  `}
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme, $backgroundImage }) => ($backgroundImage ? theme.colors.white : theme.colors.veryGray)};
  cursor: ${({ $isActive = true }) => ($isActive ? 'pointer' : 'not-allowed')};
  transition: 0.15s ease-in-out;

  &:hover {
    ${({ $backgroundImage, $isActive = true, theme }) =>
      !$backgroundImage &&
      $isActive &&
      `
      background: ${theme.colors.focoltoneWithTransparencyHover};
    `}
    border: 1px solid ${({ theme, $isActive = true, $backgroundImage }) => {
      if ($backgroundImage && $isActive) {
        return theme.colors.focoltone;
      }
      if ($backgroundImage && !$isActive) {
        return theme.colors.white;
      }
      return $isActive ? theme.colors.focoltone : theme.colors.veryGray;
    }};
  }
`;

const ContentContainer = styled.div<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  position: relative;
  z-index: 2;
  justify-content: center;
  opacity: ${({ $isActive = true }) => ($isActive ? 1 : 0.7)};
  ${({ theme: { spacing } }) => `
    padding: ${spacing(2)} ${spacing(3.5)};
    gap: ${spacing(2.5)};
  `};
  
  ${({ theme: { media, spacing } }) => `${media.large(
    () => `
      padding: ${spacing(6)};
      justify-content: flex-start;
    `,
  )}
  `};
`;

const ContentDescription = styled.div`
  display: none;
  
  ${({ theme: { media } }) => `${media.large(
    () => `
      display: block;
    `,
  )}
  `};
`;

const NavigationCard: React.FC<NavigationCardProps> = ({
  url,
  title,
  content,
  image,
  isActive = true,
  isNew = false,
}) => {
  const theme = useTheme();

  return (
    <CardLink to={url || window.location.pathname}>
      {isNew && <YellowDot width="15px" height="15px" right="-4px" top="-4px" />}
      <Container $backgroundImage={image} $isActive={isActive}>
        <ContentContainer $isActive={isActive}>
          <Typography variant="h5" color={theme.colors.white}>
            {title}
          </Typography>
          {content && (
            <ContentDescription>
              <Typography variant="caption" color={theme.colors.lightGray} htmlContent={content} />
            </ContentDescription>
          )}
        </ContentContainer>
      </Container>
    </CardLink>
  );
};

export default NavigationCard;
